import apiClient from '@/lib/api-client';

const DEFAULT_PAGE_SIZE = 1000;

/**
 * Fetch every feature from an ArcGIS FeatureServer `query` endpoint, paginating
 * until the server signals that all records have been returned.
 *
 * Termination is driven by `properties.exceededTransferLimit` (present on
 * geojson responses) rather than a fixed page size, so this works regardless
 * of the service's `maxRecordCount` (e.g. 1000 for 50 Max, 2000 for HPMV).
 *
 * The provided `baseUrl` should already contain `where`, `outFields`, and
 * `f=geojson`; only `resultOffset` and `resultRecordCount` are appended.
 */
export const fetchAllArcgisFeatures = async (
  baseUrl: string,
  pageSize: number = DEFAULT_PAGE_SIZE,
): Promise<GeoJSON.FeatureCollection> => {
  let offset = 0;
  let allFeatures: GeoJSON.Feature[] = [];

  while (true) {
    const url = new URL(baseUrl);
    url.searchParams.set('resultOffset', String(offset));
    url.searchParams.set('resultRecordCount', String(pageSize));

    const res: any = await apiClient.get(url.toString(), {
      withCredentials: false,
    });
    const features: GeoJSON.Feature[] = res?.features ?? [];
    allFeatures = allFeatures.concat(features);

    const more = res?.properties?.exceededTransferLimit === true;
    if (!more || features.length === 0) break;
    offset += features.length;
  }

  return { type: 'FeatureCollection', features: allFeatures };
};
